import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { safeAutoFocusRef } from "@/lib/safeAutoFocus";
import AssignDialog from "@/components/AssignDialog";
import { EinsatzbereichAssistent } from "@/components/EinsatzbereichAssistent";
import { PreAnalyzeDialog } from "@/components/PreAnalyzeDialog";
import { HazmatSdbSightingModal } from "@/components/HazmatSdbSightingModal";
import { ProductEvidenceDialog } from "@/components/ProductEvidenceDialog";
import { AccountPolicyBanner, usePolicyBanner } from "@/components/AccountPolicyBanner";
import { WorldQuotaBadge } from "@/components/WorldQuotaBadge";
import { useVorgaenge } from "@/hooks/useVorgaenge";
import {
  Search,
  Clock3,
  Sparkles,
  Loader2,
  Layers,
  ShieldAlert,
  Trash2,
  X,
  AlertTriangle,
  RotateCw,
  MapPin,
  ChevronDown,
  Upload,
  ScanLine,
  Archive,
  Filter,
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function matches(course, query) {
  if (!query) return true;
  const q = query.toLowerCase();
  const haystack = [
    course.title,
    course.short_description,
    ...(course.keywords || []),
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
}

function sortCourses(courses) {
  return [...courses].sort((a, b) => {
    // WELT-ARCHIVIERUNG (Feb 2026, F1–F5): archivierte immer nach unten
    // (analog Mitarbeiterliste `Mitarbeiter.jsx#filtered`).
    const aArch = a.archived ? 1 : 0;
    const bArch = b.archived ? 1 : 0;
    if (aArch !== bArch) return aArch - bArch;
    // Pending oben, dann verfügbare Welten.
    const aPending = a.generation_status === "generating" ? 0 : 1;
    const bPending = b.generation_status === "generating" ? 0 : 1;
    if (aPending !== bPending) return aPending - bPending;
    return (a.title || "").localeCompare(b.title || "", "de");
  });
}

// WELT-ARCHIVIERUNG (Feb 2026, F1–F5): Filter-Chips analog Mitarbeiterliste
// (`Mitarbeiter.jsx#FILTERS`). Reihenfolge: Aktive / Archivierte / Alle.
const ARCHIVE_FILTERS = [
  { key: "active", label: "Aktive" },
  { key: "archived", label: "Archivierte" },
  { key: "all", label: "Alle" },
];

// P0 (Juni 2026): Spiegelt das Backend-Gate (shared.course_completeness_issue).
// Eine als Welt gedachte Vorlage ohne GBU/BA – oder eine fehlgeschlagene
// KI-Generierung – darf nicht lautlos wie eine normale Unterweisung wirken.
const INCOMPLETE_MSG =
  "Diese Dokumentationswelt ist unvollständig und muss neu erzeugt werden.";

function courseIncomplete(c) {
  if (!c) return false;
  if (c.generation_status === "error") return true;
  if (c.ai_force_world && !c.is_world) return true;
  if (c.is_world && (!c.gbu || !c.betriebsanweisung)) return true;
  return false;
}

// P3-UX Schritt 1 (2026-07): Gerätebereich aus der Oberfläche entfernt.
// Der Nutzer beschreibt nur noch sein Vorhaben; Hersteller/Modell/Artikelnummer/Typenschild
// werden künftig ausschließlich über den intelligenten Rückfragedialog erhoben. Die gesamte
// Produkt-/Vision-Pipeline (productFields, buildProductContext, product_context, Typenschild,
// Badges, Upload, Backend) bleibt intern vollständig erhalten – nur die UI wird ausgeblendet.
const SHOW_DEVICE_PANEL = false;

export default function Unterweisungen() {
  const navigate = useNavigate();
  const { cancel, optimisticAdd, refreshTick } = useVorgaenge();
  const [courses, setCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [preselectedCourse, setPreselectedCourse] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [justCreatedId, setJustCreatedId] = useState(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // P2 Schritt 2: optionaler Voranalyse-Dialog (nur React-State, kein Persist).
  const [preAnalysis, setPreAnalysis] = useState(null);
  // P3-UX Schritt 5: bestätigte Typenschild-Werte werden in die Rückfragedialog-Antworten
  // vorbefüllt (Single-Source). Reiner React-State (Token erzwingt die Übernahme im Dialog).
  const [answerSeed, setAnswerSeed] = useState(null);
  // P3.1 Schritt 2: optionaler Produkt-Kontext (rein freiwillig, kein Persist/
  // LocalStorage – nur React-State). Standard eingeklappt.
  const [produktOpen, setProduktOpen] = useState(false);
  const [productFields, setProductFields] = useState({
    manufacturer: "",
    model: "",
    article_number: "",
  });
  // P3.2.2-b-1: ephemerer Typenschild-Upload + Vision-Vorschau (nur React-State,
  // kein Persist). Bild-Vorschau ist eine Client-Object-URL; nichts wird gespeichert.
  const evidenceInputRef = useRef(null);
  const [peOpen, setPeOpen] = useState(false);
  const [peLoading, setPeLoading] = useState(false);
  const [peResult, setPeResult] = useState(null);
  const [peImageUrl, setPeImageUrl] = useState(null);
  // P3.2.2-b-3: Herkunft der (übernommenen) Produktfelder – rein Frontend-State (kein Persist).
  const [productSource, setProductSource] = useState({
    manufacturer: null,
    model: null,
    article_number: null,
  });
  const { policyError, handlePolicyError, clearPolicyError } = usePolicyBanner();

  // Phase E4 – Bestands-Assistent (Dokumentation → Einsatzbereiche).
  const [luecken, setLuecken] = useState([]);
  const [einsatzbereiche, setEinsatzbereiche] = useState([]);
  const [assistentOpen, setAssistentOpen] = useState(false);
  // WELT-ARCHIVIERUNG (Feb 2026, F1–F5) – Filter-Chip-State (default „Aktive").
  const [archiveFilter, setArchiveFilter] = useState("active");
  // WELT-01b (Okt 2026) – eigenes Welten-Kontingent (read-only Anzeige).
  const [worldQuota, setWorldQuota] = useState(null);
  // WELT-02b (Betreiberfreigabe 12.10.) – Welten-Paket-Kauf (nur Zahlkunde).
  const [paketDialogOpen, setPaketDialogOpen] = useState(false);
  const [paketLoading, setPaketLoading] = useState(false);
  // Paket-Button nur für berechtigte Zahlkunden; Testkunde/ausgenommen → nichts.
  const isZahlkunde = worldQuota?.klassifikation === "zahlkunde";

  // Startet den Paket-Checkout (POST /api/checkout/welten-paket) und leitet auf
  // die Stripe-Checkout-URL weiter. Baseline der `extra`-Welten merken, damit die
  // Rückkehr-Logik die Gutschrift erkennt.
  const startWeltenPaketCheckout = async () => {
    setPaketLoading(true);
    try {
      localStorage.setItem(
        "welten_paket_baseline_extra",
        String(Number(worldQuota?.extra ?? 0))
      );
      const res = await api.createWeltenPaketCheckout(window.location.origin);
      if (res?.url) {
        window.location.href = res.url;
        return;
      }
      toast.error("Der Checkout konnte nicht gestartet werden. Bitte später erneut versuchen.");
    } catch (e) {
      if (!handlePolicyError(e)) {
        const d = e?.response?.data?.detail;
        toast.error(
          (d && (d.message || d.grund)) ||
            "Der Checkout konnte nicht gestartet werden. Bitte später erneut versuchen."
        );
      }
    } finally {
      setPaketLoading(false);
      setPaketDialogOpen(false);
    }
  };

  // Rückkehr vom Stripe-Checkout: ?paket=ok | ?paket=abbruch.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paket = params.get("paket");
    if (!paket) return;
    // URL bereinigen → keine Wiederholung bei Re-Render/Reload.
    window.history.replaceState({}, "", window.location.pathname);
    if (paket === "abbruch") {
      toast("Der Kauf wurde abgebrochen. Es wurde nichts berechnet.");
      return;
    }
    if (paket !== "ok") return;
    toast.success("Vielen Dank. Ihre 10 zusätzlichen Welten werden gutgeschrieben.");
    const baseline = Number(localStorage.getItem("welten_paket_baseline_extra") ?? "0");
    localStorage.removeItem("welten_paket_baseline_extra");
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const q = await api.getWorldQuota();
        if (cancelled) return;
        setWorldQuota(q);
        if (Number(q?.extra ?? 0) > baseline) return; // Gutschrift sichtbar → fertig
      } catch {
        /* Anzeige ist optional – Fehler still verschlucken */
      }
      if (cancelled) return;
      if (attempts >= 3) {
        toast("Die Gutschrift kann einige Minuten dauern.");
        return;
      }
      setTimeout(poll, 2500);
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLuecken = () => {
    api.listEinsatzbereichLuecken().then((res) => setLuecken(res?.items || []));
  };

  useEffect(() => {
    loadLuecken();
    api.listEinsatzbereiche(true).then((ebs) => setEinsatzbereiche(ebs || []));
  }, [refreshTick]);

  // Liste inklusive Pending-Stubs holen, damit die Ghost-Card direkt im Listen-
  // Strom auftaucht. refreshTick bumpst bei Job-Übergängen (Generierung fertig
  // / Fehler / Abbruch) → neu laden.
  // WELT-ARCHIVIERUNG (Feb 2026, F1–F5): Bei Filter „Archivierte" oder „Alle"
  // die Liste inkl. archivierter Welten holen; sonst backend-seitig gefiltert.
  useEffect(() => {
    let cancelled = false;
    const includeArchived = archiveFilter !== "active";
    api.listCourses(true, includeArchived).then((data) => {
      if (!cancelled) setCourses(data);
    });
    // WELT-01b: Welten-Kontingent laden; refreshTick aktualisiert nach jeder
    // Generierung. Fehler werden still verschluckt (Anzeige ist optional). Der
    // Guard hält bestehende Tests grün, die `api` nur partiell mocken.
    if (typeof api.getWorldQuota === "function") {
      api
        .getWorldQuota()
        .then((q) => {
          if (!cancelled) setWorldQuota(q);
        })
        .catch(() => {});
    }
    return () => {
      cancelled = true;
    };
  }, [refreshTick, archiveFilter]);

  const filtered = useMemo(() => {
    let list = courses.filter((c) => matches(c, searchTerm));
    if (archiveFilter === "archived") list = list.filter((c) => c.archived);
    else if (archiveFilter === "active") list = list.filter((c) => !c.archived);
    return sortCourses(list);
  }, [courses, searchTerm, archiveFilter]);

  const archiveCounts = useMemo(() => {
    const active = courses.filter((c) => !c.archived).length;
    const archived = courses.filter((c) => c.archived).length;
    return { active, archived, all: courses.length };
  }, [courses]);

  const trimmedTerm = searchTerm.trim();
  const noResults = trimmedTerm.length > 0 && filtered.length === 0;

  const startAssign = (course) => {
    if (courseIncomplete(course)) {
      toast.error(INCOMPLETE_MSG);
      return;
    }
    if (course.is_world && !course.released) {
      navigate(`/unternehmer/welt/${course.id}`);
      return;
    }
    setPreselectedCourse(course);
    setAssignOpen(true);
  };

  // P3.1 Schritt 2: baut den transienten Produkt-Kontext aus den optionalen
  // Feldern (nur nicht-leere Werte). Kein Persist – reiner Request-Kontext.
  const buildProductContext = () => {
    const pc = {};
    const m = productFields.manufacturer.trim();
    const mo = productFields.model.trim();
    const an = productFields.article_number.trim();
    if (m) pc.manufacturer = m;
    if (mo) pc.model = mo;
    if (an) pc.article_number = an;
    return Object.keys(pc).length > 0 ? pc : null;
  };

  // P3.2.2-b-3: manuelle Änderung eines Produktfeldes löscht dessen „Typenschild"-Herkunft
  // (der Wert stammt dann nicht mehr rein aus dem Foto).
  const updateProductField = (name, value) => {
    setProductFields((f) => ({ ...f, [name]: value }));
    setProductSource((s) => (s[name] ? { ...s, [name]: null } : s));
  };

  // P3.2.2-b-1: Typenschild-Foto ephemer analysieren. Öffnet den Bestätigungs-
  // dialog (Vorschau + Vorschläge + Provenienz). Fail-soft: Policy-Fehler → Banner,
  // sonstiger Fehler → fail-closed Hinweis im Dialog. KEIN Persist, KEIN product_context.
  const openEvidencePicker = () => {
    clearPolicyError();
    evidenceInputRef.current?.click();
  };
  const closeEvidence = () => {
    setPeOpen(false);
    if (peImageUrl) URL.revokeObjectURL(peImageUrl);
    setPeImageUrl(null);
    setPeResult(null);
    setPeLoading(false);
  };
  const handleEvidenceFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // gleiche Datei erneut wählbar machen
    if (!file) return;
    const url = URL.createObjectURL(file);
    setPeImageUrl(url);
    setPeResult(null);
    setPeLoading(true);
    setPeOpen(true);
    try {
      const res = await api.analyzeProductEvidence(file);
      setPeResult(res);
    } catch (err) {
      if (handlePolicyError(err)) {
        closeEvidence();
      } else {
        setPeResult({
          suggestions: {},
          source_notes: [
            "Analyse nicht möglich – bitte später erneut versuchen oder Werte manuell erfassen.",
          ],
          provenance: {},
        });
      }
    } finally {
      setPeLoading(false);
    }
  };
  // P3.2.2-b-3: bewusste Übernahme – NUR die drei bekannten Felder fließen in
  // productFields (→ bestehende product_context-Pipeline). Technische Felder werden
  // NICHT übernommen. Nur nicht-leere Werte werden gesetzt + als „Typenschild" markiert.
  const handleEvidenceApply = (values) => {
    // P3-UX Schritt 5: NUR die 3 bekannten Felder, nur nicht-leer → als Seed in die
    // Rückfragedialog-Antworten (Single-Source). productFields wird NICHT mehr beschrieben.
    const seedValues = {};
    for (const key of ["manufacturer", "model", "article_number"]) {
      const v = (values?.[key] || "").trim();
      if (v) seedValues[key] = v;
    }
    if (Object.keys(seedValues).length > 0) {
      setAnswerSeed({ values: seedValues, source: "Typenschild", token: Date.now() });
    }
    closeEvidence();
  };

  // Der eigentliche, bestehende Generierungs-Aufruf. `context` ist OPTIONAL
  // (P2 Schritt 4): liegt er vor, wird der Voranalyse-Kontext übergeben; ohne
  // Kontext bleibt der Aufruf byte-identisch zum bisherigen Flow. P3.1 Schritt 2:
  // ein optional befüllter Produkt-Kontext wird transient beigemischt.
  const runGenerate = async (topic, context = null) => {
    clearPolicyError();
    const productContext = buildProductContext();
    const mergedContext = productContext
      ? { ...(context || {}), product_context: productContext }
      : context;
    try {
      const pending = mergedContext
        ? await api.generateCourse(topic, mergedContext)
        : await api.generateCourse(topic);
      // Optimistische Ghost-Card in Tray UND Bibliothek-Liste
      optimisticAdd({
        id: pending.id,
        title: `Wird erstellt: ${topic}`,
        topic,
      });
      // Sofort frisch laden, damit der Stub aus dem Server in der Liste landet
      const fresh = await api.listCourses(true, archiveFilter !== "active");
      setCourses(fresh);
      setSearchTerm("");
      toast.success(
        `${"\u201E"}${topic}${"\u201C"} wird im Hintergrund erstellt – Sie können weiterarbeiten.`,
        { duration: 6000 }
      );
    } catch (err) {
      if (!handlePolicyError(err)) {
        toast.error(
          err?.response?.data?.detail || "KI konnte keine Unterweisung erstellen."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  // P2 Schritt 2: optionale Voranalyse VOR der Generierung. Fail-soft – ein
  // Fehler der Voranalyse darf den bestehenden Erstell-Ablauf NIE blockieren.
  // Phase A · Schritt D (22.08.2026): wenn die Preanalyse einen Gefahrstoff
  // meldet (`hazmat_signal=true`), wird das Modal IMMER geöffnet — unabhängig
  // davon, ob `needs_questions=true` gilt.
  //
  // Betreiberentscheidung 24.08.2026: bei hazmat_signal=true öffnen wir
  // DIREKT das kombinierte Modal (`HazmatSdbSightingModal`) mit Pflichtangaben
  // + Drag-&-Drop + Auto-Suche in einem Rutsch — kein Zwischen-Dialog mehr.
  // Cleanup 30.08.2026: Der frühere PreAnalyzeDialog-hazmat-Zweig (toter
  // Code seit 24.08.) wurde vollständig ausgebaut — die Substitutionsprüfung
  // existiert nur noch im HazmatSdbSightingModal.
  const handleGenerate = async () => {
    if (!trimmedTerm || submitting) return;
    const topic = trimmedTerm;
    setSubmitting(true);
    let analysis = null;
    try {
      analysis = await api.preAnalyze(topic);
    } catch {
      analysis = null;
    }
    const isHazmat = !!analysis?.hazmat_signal;
    const hasQuestions =
      analysis?.needs_questions &&
      Array.isArray(analysis?.questions) &&
      analysis.questions.length > 0;
    if (analysis && isHazmat) {
      // Kombiniertes Modal übernimmt Pflichtangaben + SDB-Suche/Upload.
      setSdbCombinedInitial({
        topic,
        quantityQuestion: analysis.quantity_question || null,
        seedModel: (answerSeed?.values?.model || "").trim() || topic,
      });
      setSubmitting(false);
      return;
    }
    if (analysis && hasQuestions) {
      setPreAnalysis({ topic, ...analysis });
      setSubmitting(false);
      return;
    }
    await runGenerate(topic);
  };

  // Dialog-Callbacks. „Direkt erstellen" nutzt den UNVERÄNDERTEN generateCourse(topic)
  // OHNE Kontext. „Mit Antworten erstellen" (P2 Schritt 4) übergibt zusätzlich den
  // flüchtigen Voranalyse-Kontext (nur nicht-leere Antworten, Reihenfolge erhalten;
  // kein Persist, kein Draft).
  const preAnalyzeDirect = () => {
    const topic = preAnalysis?.topic;
    setPreAnalysis(null);
    setAnswerSeed(null);
    if (!topic) return;
    setSubmitting(true);
    runGenerate(topic);
  };
  const preAnalyzeWithAnswers = (answers) => {
    const topic = preAnalysis?.topic;
    const candidates = preAnalysis?.candidate_activity_classes;
    const uncertain = preAnalysis?.uncertain_activity_classes;
    setPreAnalysis(null);
    if (!topic) return;
    setSubmitting(true);
    // P3-UX: Gerätefragen (maps_to product.*) fliessen in den bestehenden product_context,
    // alle uebrigen Antworten bleiben clarifying_answers. Nur nicht-leere Antworten.
    const PRODUCT_MAP = {
      "product.manufacturer": "manufacturer",
      "product.model": "model",
      "product.article_number": "article_number",
    };
    const clarifying_answers = [];
    const product = {};
    for (const a of answers || []) {
      const value = (a?.answer || "").trim();
      if (!value) continue;
      const key = PRODUCT_MAP[a?.maps_to];
      if (key) product[key] = value;
      else clarifying_answers.push({ question: a.question, answer: value });
    }
    const context = {};
    if (clarifying_answers.length > 0) context.clarifying_answers = clarifying_answers;
    if (Object.keys(product).length > 0) context.product_context = product;
    if (Array.isArray(candidates) && candidates.length > 0)
      context.candidate_activity_classes = candidates;
    if (Array.isArray(uncertain) && uncertain.length > 0)
      context.uncertain_activity_classes = uncertain;
    runGenerate(topic, Object.keys(context).length > 0 ? context : null);
  };
  const preAnalyzeCancel = () => {
    setPreAnalysis(null);
    setAnswerSeed(null);
    setSubmitting(false);
  };

  // Phase A · Schritt D (Gefahrstoffmodul, 22.08.2026): Gefahrstoff-Erklärung
  // wird als Teil des `context` an `runGenerate` → `api.generateCourse`
  // durchgereicht. Backend legt daraufhin WORM + Registry synchron an
  // (F-D1=a), bevor die Weltgenerierung startet.
  //
  // Betreiberentscheidung 24.08.2026 · Kombiniertes Modal: statt zweier
  // getrennter Dialoge (PreAnalyzeDialog + HazmatSdbSightingModal) läuft
  // beides jetzt in einem einzigen Modal, das direkt bei hazmat_signal=true
  // geöffnet wird. Ergebnis-Callback bekommt bereits die vollständige
  // Declaration + optional die prefetch_id (Auto-Fund oder Upload).
  const [sdbCombinedInitial, setSdbCombinedInitial] = useState(null);

  const sdbCombinedConfirm = (declaration, prefetchId) => {
    const topic = sdbCombinedInitial?.topic;
    setSdbCombinedInitial(null);
    if (!topic) return;
    setSubmitting(true);
    const ctx = {
      hazmat_declaration: declaration,
      preanalysis_had_hazmat_signal: true,
    };
    if (prefetchId) ctx.sdb_prefetch_id = prefetchId;
    runGenerate(topic, ctx);
  };
  // „Ohne SDB fortfahren" (sdbCombinedSkipSdb) entfernt — Betreiber-
  // entscheidung 30.08.2026: keine Registry-„Waisen" ohne SDB-Daten mehr.
  const sdbCombinedCancel = () => {
    setSdbCombinedInitial(null);
    setSubmitting(false);
  };
  const sdbCombinedFleeHazmat = () => {
    const topic = sdbCombinedInitial?.topic;
    setSdbCombinedInitial(null);
    if (!topic) return;
    setSubmitting(true);
    runGenerate(topic, {
      preanalysis_had_hazmat_signal: true,
      flee_hazmat: true,
    });
  };

  // Cleanup 30.08.2026: Die alten SDB-Sichtungs-Callbacks des toten
  // PreAnalyzeDialog-hazmat-Zweigs (preAnalyzeHazmatDeclare,
  // preAnalyzeFleeHazmat, nie gelesener Slot `sdbSightingDeclaration`)
  // wurden vollständig ausgebaut. Der Fluchtweg „Kein Gefahrstoff" lebt
  // im kombinierten Modal (sdbCombinedFleeHazmat).

  const confirmDelete = async () => {
    if (!deleteCandidate) return;
    setDeleting(true);
    try {
      await api.deleteCourse(deleteCandidate.id);
      setCourses((cs) => cs.filter((c) => c.id !== deleteCandidate.id));
      toast.success("Welt verworfen.");
      setDeleteCandidate(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verwerfen fehlgeschlagen.");
    } finally {
      setDeleting(false);
    }
  };

  // Sprint Q (Juni 2026): einen fehlgeschlagenen Welt-Stub erneut erzeugen –
  // nutzt die bestehende Pipeline (Backend `/courses/{id}/retry`).
  const retryIncomplete = async (course) => {
    clearPolicyError();
    try {
      await api.retryCourse(course.id);
      optimisticAdd({
        id: course.id,
        title: `Wird erstellt: ${course.ai_input_term || course.title}`,
        topic: course.ai_input_term || "",
      });
      const fresh = await api.listCourses(true);
      setCourses(fresh);
      toast.success("Welt wird erneut erzeugt – Sie können weiterarbeiten.", {
        duration: 5000,
      });
    } catch (e) {
      if (!handlePolicyError(e)) {
        toast.error(e?.response?.data?.detail || "Neu-Erzeugung fehlgeschlagen.");
      }
    }
  };

  // Cancel direkt aus der Ghost-Card heraus – delegiert an den Vorgangs-Hook,
  // der auch den Tray-Eintrag sauber entfernt.
  const cancelPending = async (course) => {
    await cancel(course.id);
    setCourses((cs) => cs.filter((c) => c.id !== course.id));
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="bibliothek-page">
      {/* Hero / Suchfeld */}
      <div>
        <p className="text-xs uppercase tracking-widest text-slate-500 font-medium">
          Gefährdungsbeurteilungen
        </p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-2">
          Was möchten Sie finden?
        </h1>
        <p className="text-slate-600 mt-2 max-w-xl">
          Tippen Sie ein – das System findet die passende Vorlage. Wenn keine
          vorhanden ist, wird die komplette Dokumentationswelt erstellt.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="world-quota-wrap">
          <WorldQuotaBadge quota={worldQuota} />
          {isZahlkunde && (
            <button
              type="button"
              data-testid="welten-paket-library-button"
              onClick={() => setPaketDialogOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-800 hover:bg-emerald-100 transition-colors"
            >
              <Layers className="w-4 h-4 shrink-0" />
              Weitere Welten: 10 für 79 € netto
            </button>
          )}
        </div>

        <div className="mt-6 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="z. B. Altendorf F45, Kreissäge, Aceton…"
            data-testid="library-search-input"
            ref={safeAutoFocusRef}
            className="h-14 pl-12 text-base bg-white border-slate-300 shadow-sm focus-visible:ring-slate-900"
          />
        </div>

        {/* WELT-ARCHIVIERUNG (Feb 2026, F1–F5) — Filter-Chips analog
            Mitarbeiterliste. Backend-seitiger `include_archived`-Filter reicht
            die Auswahl durch (siehe useEffect oben). */}
        <div className="mt-3 flex items-center gap-2" data-testid="library-archive-filters">
          <Filter className="w-4 h-4 text-slate-500 ml-1" />
          {ARCHIVE_FILTERS.map((f) => {
            const c =
              f.key === "active"
                ? archiveCounts.active
                : f.key === "archived"
                  ? archiveCounts.archived
                  : archiveCounts.all;
            const active = archiveFilter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setArchiveFilter(f.key)}
                data-testid={`library-archive-filter-${f.key}`}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors inline-flex items-center gap-2 ${
                  active
                    ? "bg-slate-900 text-white"
                    : "text-slate-700 hover:bg-slate-100"
                }`}
              >
                {f.label}
                <span
                  className={`text-xs px-1.5 py-0.5 rounded ${
                    active ? "bg-white/10" : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {c}
                </span>
              </button>
            );
          })}
        </div>

        {/* P3-UX Schritt 1 (2026-07): Gerätebereich per Flag aus der UI entfernt.
            Gesamte Produkt-/Typenschild-Pipeline bleibt intern erhalten (SHOW_DEVICE_PANEL).
            Gerätedaten werden künftig über den intelligenten Rückfragedialog erhoben. */}
        {SHOW_DEVICE_PANEL && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setProduktOpen((v) => !v)}
            data-testid="produkt-praezisieren-toggle"
            aria-expanded={produktOpen}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ChevronDown
              className={`w-4 h-4 transition-transform ${produktOpen ? "" : "-rotate-90"}`}
            />
            Gerät präzisieren (optional)
          </button>

          {produktOpen && (
            <div
              className="mt-3 grid gap-3 sm:grid-cols-3 rounded-lg border border-slate-200 bg-slate-50/60 p-4"
              data-testid="produkt-praezisieren-panel"
            >
              <div>
                <label className="text-xs font-medium text-slate-500">Hersteller</label>
                <Input
                  value={productFields.manufacturer}
                  onChange={(e) => updateProductField("manufacturer", e.target.value)}
                  placeholder="z. B. Bosch"
                  data-testid="produkt-manufacturer-input"
                  className="mt-1 bg-white border-slate-300"
                />
                {productSource.manufacturer && (
                  <Badge
                    variant="outline"
                    data-testid="produkt-source-badge-manufacturer"
                    className="mt-1 inline-flex items-center gap-1 bg-slate-50 border-slate-200 text-slate-500 text-[10px]"
                  >
                    <ScanLine className="w-3 h-3" />
                    {productSource.manufacturer}
                  </Badge>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Modell</label>
                <Input
                  value={productFields.model}
                  onChange={(e) => updateProductField("model", e.target.value)}
                  placeholder="z. B. GWS 750"
                  data-testid="produkt-model-input"
                  className="mt-1 bg-white border-slate-300"
                />
                {productSource.model && (
                  <Badge
                    variant="outline"
                    data-testid="produkt-source-badge-model"
                    className="mt-1 inline-flex items-center gap-1 bg-slate-50 border-slate-200 text-slate-500 text-[10px]"
                  >
                    <ScanLine className="w-3 h-3" />
                    {productSource.model}
                  </Badge>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Artikelnummer</label>
                <Input
                  value={productFields.article_number}
                  onChange={(e) => updateProductField("article_number", e.target.value)}
                  placeholder="z. B. 0601394000"
                  data-testid="produkt-article-number-input"
                  className="mt-1 bg-white border-slate-300"
                />
                {productSource.article_number && (
                  <Badge
                    variant="outline"
                    data-testid="produkt-source-badge-article_number"
                    className="mt-1 inline-flex items-center gap-1 bg-slate-50 border-slate-200 text-slate-500 text-[10px]"
                  >
                    <ScanLine className="w-3 h-3" />
                    {productSource.article_number}
                  </Badge>
                )}
              </div>

              {/* P3.2.2-b-1: optionaler Typenschild-Upload (ephemer, füllt vorerst nichts aus) */}
              <div className="sm:col-span-3 pt-3 border-t border-slate-200">
                <input
                  ref={evidenceInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleEvidenceFile}
                  data-testid="produkt-typenschild-file-input"
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={openEvidencePicker}
                  data-testid="produkt-typenschild-upload-button"
                  className="h-10 border-slate-300 text-slate-700 hover:bg-white"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Typenschild-Foto hochladen
                </Button>
                <p className="text-[11px] text-slate-400 mt-1.5">
                  Optional – wird nur gelesen, nicht gespeichert (JPEG/PNG/WEBP).
                </p>
              </div>
            </div>
          )}
        </div>
        )}
      </div>

      {policyError && (
        <AccountPolicyBanner
          error={policyError}
          onDismiss={clearPolicyError}
          onBuyPaket={() => setPaketDialogOpen(true)}
        />
      )}

      {/* WELT-02b (Betreiberfreigabe 12.10.) – Hinweis vor Weiterleitung zum Paket-Checkout. */}
      <AlertDialog open={paketDialogOpen} onOpenChange={setPaketDialogOpen}>
        <AlertDialogContent data-testid="welten-paket-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Welten-Paket kaufen</AlertDialogTitle>
            <AlertDialogDescription data-testid="welten-paket-dialog-hint">
              10 zusätzliche eigene Welten, einmalig 79,00 € netto zzgl. MwSt.
              Zahlung per Karte, Apple Pay oder Google Pay. Pakete verfallen nicht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="welten-paket-dialog-cancel" disabled={paketLoading}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="welten-paket-dialog-confirm"
              disabled={paketLoading}
              onClick={(e) => {
                e.preventDefault();
                startWeltenPaketCheckout();
              }}
            >
              {paketLoading ? "Wird geöffnet…" : "Zum Checkout"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {luecken.length > 0 && (
        <Card
          className="bg-amber-50 border border-amber-200 shadow-sm p-4 sm:p-5"
          data-testid="einsatzbereich-luecken-banner"
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 shrink-0">
              <MapPin className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading font-semibold text-slate-900">
                {luecken.length} Dokumentation
                {luecken.length === 1 ? "" : "en"} noch ohne Einsatzbereich
              </p>
              <p className="text-sm text-slate-600 mt-0.5">
                Ordnen Sie sie einmalig zu – danach erhalten neue Mitarbeiter
                automatisch die passenden Dokumentationen.
              </p>
            </div>
            <Button
              onClick={() => setAssistentOpen(true)}
              data-testid="open-einsatzbereich-assistent"
              className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
            >
              Jetzt zuordnen
            </Button>
          </div>
        </Card>
      )}

      <EinsatzbereichAssistent
        open={assistentOpen}
        onOpenChange={setAssistentOpen}
        items={luecken}
        einsatzbereiche={einsatzbereiche}
        onCompleted={loadLuecken}
      />

      {/* Ergebnisliste oder Empty-State */}
      {noResults ? (
        <Card
          className="bg-white border border-slate-200 shadow-sm p-10 text-center"
          data-testid="library-no-results"
        >
          <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 flex items-center justify-center text-slate-500 mb-4">
            <Sparkles className="w-6 h-6" />
          </div>
          <h3 className="font-heading font-semibold text-slate-900">
            Für {"\u201E"}
            {trimmedTerm}
            {"\u201C"} ist noch keine Vorlage da.
          </h3>
          <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
            Das System kann die passende Dokumentationswelt in wenigen Sekunden
            erstellen – inklusive Gefährdungsbeurteilung, Betriebsanweisung,
            Unterweisung und Wartungshinweisen.
          </p>
          <Button
            onClick={handleGenerate}
            disabled={submitting}
            data-testid="library-generate-button"
            className="mt-6 bg-slate-900 hover:bg-slate-800 text-white h-11 px-5"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {submitting
              ? "Starte Generierung…"
              : `Dokumentationswelt zu „${trimmedTerm}" erstellen lassen`}
          </Button>
        </Card>
      ) : (
        <Card
          className="bg-white border border-slate-200 shadow-sm overflow-hidden"
          data-testid="library-results"
        >
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between text-sm">
            <span className="text-slate-500">
              {trimmedTerm
                ? `${filtered.length} Treffer für „${trimmedTerm}"`
                : `${filtered.length} Vorlagen in den Gefährdungsbeurteilungen`}
            </span>
          </div>

          <div className="divide-y divide-slate-100">
            {filtered.map((c) => {
              if (c.generation_status === "generating") {
                return (
                  <PendingRow
                    key={c.id}
                    course={c}
                    onCancel={() => cancelPending(c)}
                  />
                );
              }
              const isJustCreated = c.id === justCreatedId;
              const isPendingRelease = c.is_world && !c.released;
              const incomplete = courseIncomplete(c);
              const isArchived = !!c.archived;
              return (
                <div
                  key={c.id}
                  data-testid={`library-row-${c.id}`}
                  className={`px-6 py-5 flex items-center justify-between gap-4 transition-colors ${
                    incomplete
                      ? "bg-red-50/60 ring-1 ring-red-200"
                      : isJustCreated
                        ? "bg-emerald-50/60 ring-1 ring-emerald-200"
                        : isArchived
                          ? "opacity-60 hover:bg-slate-50"
                          : "hover:bg-slate-50"
                  }`}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div
                      className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${
                        incomplete
                          ? "bg-red-600 text-white"
                          : "bg-indigo-600 text-white"
                      }`}
                    >
                      {incomplete ? (
                        <AlertTriangle className="w-5 h-5" />
                      ) : (
                        <Layers className="w-5 h-5" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-medium truncate text-slate-900">
                          {c.title}
                        </div>
                        {incomplete && (
                          <Badge
                            className="bg-red-600 text-white hover:bg-red-600 text-[10px] uppercase tracking-wider"
                            data-testid={`library-incomplete-badge-${c.id}`}
                          >
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Unvollständig
                          </Badge>
                        )}
                        {c.is_world && (
                          <Badge
                            variant="outline"
                            className="bg-indigo-50 border-indigo-200 text-indigo-800 text-[10px] uppercase tracking-wider"
                            data-testid={`library-world-badge-${c.id}`}
                          >
                            <Layers className="w-3 h-3 mr-1" />
                            Welt
                          </Badge>
                        )}
                        {isPendingRelease && (
                          <Badge
                            className="bg-amber-500 text-white hover:bg-amber-500 text-[10px] uppercase tracking-wider"
                            data-testid={`library-pending-release-${c.id}`}
                          >
                            <ShieldAlert className="w-3 h-3 mr-1" />
                            Freigabe ausstehend
                          </Badge>
                        )}
                        {isJustCreated && (
                          <Badge
                            className="bg-emerald-600 text-white hover:bg-emerald-600 text-[10px] uppercase tracking-wider"
                            data-testid={`library-just-created-${c.id}`}
                          >
                            <Sparkles className="w-3 h-3 mr-1" />
                            Soeben erstellt
                          </Badge>
                        )}
                        {isArchived && (
                          <Badge
                            className="bg-slate-200 hover:bg-slate-200 text-slate-700 text-[10px] uppercase tracking-wider"
                            data-testid={`library-archived-badge-${c.id}`}
                          >
                            <Archive className="w-3 h-3 mr-1" />
                            Archiviert
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-slate-500 truncate">
                        {c.short_description}
                      </div>
                      {!incomplete && (
                        <div className="text-xs text-slate-400 mt-1 inline-flex items-center gap-1.5">
                          <Clock3 className="w-3 h-3" />~ {c.duration_minutes} Min.
                          Lesezeit
                          <span className="text-slate-300">·</span>
                          Version {c.version}
                          {c.source === "ai_generated" && (
                            <>
                              <span className="text-slate-300">·</span>
                              <span className="inline-flex items-center gap-1">
                                <Sparkles className="w-3 h-3" />
                                CLartext.io-erstellt
                              </span>
                              {c.ai_input_term && (
                                <>
                                  <span className="text-slate-300">·</span>
                                  <span
                                    className="truncate max-w-[240px]"
                                    data-testid={`library-input-term-${c.id}`}
                                    title={`Erstellt aus Eingabe: ${c.ai_input_term}`}
                                  >
                                    aus Eingabe: {c.ai_input_term}
                                  </span>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="shrink-0 flex items-center gap-2">
                    {incomplete ? (
                      <>
                        <Button
                          variant="outline"
                          onClick={() => setDeleteCandidate(c)}
                          data-testid={`library-discard-incomplete-${c.id}`}
                          className="h-10 w-10 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-200"
                          aria-label="Verwerfen"
                          title="Unvollständige Welt verwerfen"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => retryIncomplete(c)}
                          data-testid={`library-retry-incomplete-${c.id}`}
                          className="h-10 px-4 text-indigo-700 border-indigo-200 hover:bg-indigo-50 hover:text-indigo-800"
                          title="Welt erneut erzeugen"
                        >
                          <RotateCw className="w-4 h-4 mr-1.5" />
                          Neu erzeugen
                        </Button>
                      </>
                    ) : c.is_world ? (
                      isPendingRelease ? (
                        <>
                          <Button
                            variant="outline"
                            onClick={() => setDeleteCandidate(c)}
                            data-testid={`library-delete-${c.id}`}
                            className="h-10 w-10 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-200"
                            aria-label="Verwerfen"
                            title="Welt verwerfen"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => navigate(`/unternehmer/welt/${c.id}`)}
                            data-testid={`library-review-${c.id}`}
                            className="bg-amber-600 hover:bg-amber-700 text-white h-10 px-4"
                          >
                            Prüfen & freigeben
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="outline"
                            onClick={() => navigate(`/unternehmer/welt/${c.id}`)}
                            data-testid={`library-view-${c.id}`}
                            className="h-10 px-3"
                          >
                            Ansehen
                          </Button>
                          {/* WELT-ARCHIVIERUNG (Feb 2026, F1–F5): kein „Sofort
                              zuweisen" für archivierte Welten — Reaktivieren
                              läuft über die Welt-Detailseite. */}
                          {!isArchived && (
                            <Button
                              onClick={() => startAssign(c)}
                              data-testid={`library-assign-${c.id}`}
                              className="bg-slate-900 hover:bg-slate-800 text-white h-10 px-4"
                            >
                              Sofort zuweisen
                            </Button>
                          )}
                        </>
                      )
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <AssignDialog
        open={assignOpen}
        onOpenChange={(v) => {
          setAssignOpen(v);
          if (!v) setPreselectedCourse(null);
        }}
        courseId={preselectedCourse?.id}
        courseTitle={preselectedCourse?.title}
        courseRecurrence={preselectedCourse?.recurrence}
        courseRecurrenceReason={preselectedCourse?.ai_recurrence_reason}
        courseEinsatzbereichIds={preselectedCourse?.einsatzbereich_ids || []}
      />

      {/* P3-UX Schritt 5: immer gerenderter versteckter Datei-Input – der Upload-Button lebt
          jetzt im Rückfragedialog (der frühere lag im ausgeblendeten Gerätepanel). */}
      <input
        ref={evidenceInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleEvidenceFile}
        data-testid="produkt-typenschild-file-input"
        className="hidden"
      />

      <PreAnalyzeDialog
        data={preAnalysis}
        submitting={submitting}
        onDirectCreate={preAnalyzeDirect}
        onCreateWithAnswers={preAnalyzeWithAnswers}
        onCancel={preAnalyzeCancel}
        onUploadNameplate={openEvidencePicker}
        seed={answerSeed}
      />

      {/* Kombiniertes Sichtungs-Modal (Betreiberentscheidung 24.08.2026):
          Pflichtangaben + Drag-&-Drop + Auto-Suche in einem Modal. */}
      <HazmatSdbSightingModal
        open={!!sdbCombinedInitial}
        initial={sdbCombinedInitial}
        onConfirm={sdbCombinedConfirm}
        onCancel={sdbCombinedCancel}
        onFleeHazmat={sdbCombinedFleeHazmat}
      />

      <ProductEvidenceDialog
        open={peOpen}
        loading={peLoading}
        data={peResult}
        imageUrl={peImageUrl}
        onClose={closeEvidence}
        onApply={handleEvidenceApply}
      />

      <AlertDialog
        open={!!deleteCandidate}
        onOpenChange={(v) => !v && setDeleteCandidate(null)}
      >
        <AlertDialogContent data-testid="library-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Welt wirklich verwerfen?</AlertDialogTitle>
            <AlertDialogDescription>
              {"\u201E"}
              {deleteCandidate?.title}
              {"\u201C"} wird unwiderruflich gelöscht. Alle generierten
              Bausteine (Gefährdungsbeurteilung, Betriebsanweisung,
              Verständnisfragen …) gehen verloren. Das ist nur möglich,
              solange die Welt noch nicht freigegeben wurde.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="library-delete-cancel">
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              data-testid="library-delete-confirm-button"
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? "Wird verworfen…" : "Endgültig verwerfen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ghost-Card für eine laufende KI-Generierung. Bewusst in der Listen-Strecke
// (nicht als Loader-Overlay), damit der User die übrige Bibliothek nutzen kann.
// ---------------------------------------------------------------------------
function PendingRow({ course, onCancel }) {
  const startedAtMs = course.ai_generated_at
    ? new Date(course.ai_generated_at).getTime()
    : null;
  // Tick-State, damit der Render rein aus State liest und Date.now() nicht
  // mehr im Render-Body steht (React-19-Purity-Regel).
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!startedAtMs) return;
    const update = () =>
      setElapsed(Math.max(0, Math.floor((Date.now() - startedAtMs) / 1000)));
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [startedAtMs]);

  // Ghost-Card-Fix Teil 2 (23.08.2026): Notfall-Direktabfrage. Der lokale
  // Stoppuhr-Timer läuft unabhängig vom zentralen Job-Poller
  // (`useVorgaenge.jsx`). Wenn dieser aus irgendeinem Grund den fertig-
  // Übergang verpasst hat (Browser-Tab-Throttling, Netz-Aussetzer,
  // 30-s-Feed-Fenster-Race), bleibt die Karte sonst ewig stehen. Ab 150 s
  // (~2× real gemessene Mediandauer) fragen wir alle 30 s direkt am Course-
  // Doc nach. Bei ready/error/deleted bumpen wir `refreshTick` (analog zur
  // Verschwunden-Regel in useVorgaenge), damit die Liste sich aktualisiert.
  // Kein Toast — der Karten-Ersatz ist Anzeige genug.
  const { triggerRefresh } = useVorgaenge();
  useEffect(() => {
    if (elapsed < 150) return;
    let cancelled = false;
    let handle = null;
    const probe = async () => {
      if (cancelled) return;
      try {
        const doc = await api.getCourse(course.id);
        const st = doc?.generation_status;
        if (st === "ready" || st === "error") {
          triggerRefresh();
          return; // beim nächsten Poll ist Karte weg
        }
      } catch (e) {
        // 404 = Job wurde gelöscht → Liste aktualisieren, Karte verschwindet.
        if (e?.response?.status === 404) {
          triggerRefresh();
          return;
        }
        // andere Fehler still schlucken, nächster Versuch in 30 s
      }
      if (!cancelled) handle = setTimeout(probe, 30000);
    };
    // Beim Übertreten der Schwelle sofort einen ersten Direkt-Ping absetzen.
    handle = setTimeout(probe, 0);
    return () => {
      cancelled = true;
      if (handle) clearTimeout(handle);
    };
  }, [elapsed >= 150, course.id, triggerRefresh]);

  const topic =
    course.ai_input_term || course.title?.replace(/^Wird erstellt:\s*/, "");

  return (
    <div
      data-testid={`library-pending-row-${course.id}`}
      className="px-6 py-5 flex items-center justify-between gap-4 bg-sky-50/60 ring-1 ring-sky-200"
    >
      <div className="flex items-center gap-4 min-w-0">
        <div className="w-10 h-10 rounded-md flex items-center justify-center shrink-0 bg-sky-100 text-sky-700">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-medium text-slate-900 truncate">
              Wird erstellt: {topic}
            </div>
            <Badge
              variant="outline"
              className="bg-sky-100 border-sky-200 text-sky-800 text-[10px] uppercase tracking-wider inline-flex items-center gap-1"
            >
              <Sparkles className="w-3 h-3" />
              KI generiert
            </Badge>
          </div>
          <div className="text-sm text-slate-500 mt-0.5">
            Läuft im Hintergrund – Sie können weiterarbeiten. {elapsed} s
          </div>
          <div className="text-xs text-slate-400 mt-1 inline-flex items-center gap-1.5">
            <AlertTriangle className="w-3 h-3 text-amber-500" />
            {/* Ghost-Card-Fix (23.08.2026): Text an heutige Realität
                angepasst. Echte POST-→-ready-Zeiten liegen im Median bei
                65–75 s, mit Bild-Manifest-Nachlauf teils bis ~145 s. Die
                alte Angabe „30–90 s" war schon nach 90 s als Ausreißer
                erkennbar — jetzt konservativer formuliert. */}
            Dauer typischerweise 1–2 Minuten
          </div>
        </div>
      </div>
      <div className="shrink-0">
        <Button
          variant="outline"
          onClick={onCancel}
          data-testid={`library-pending-cancel-${course.id}`}
          className="h-9 px-3 text-slate-600 hover:text-rose-700 hover:bg-rose-50 hover:border-rose-200"
        >
          <X className="w-4 h-4 mr-1.5" />
          Abbrechen
        </Button>
      </div>
    </div>
  );
}
